/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { useBrushEventListeners } from 'src/modules/labeling/hooks/useBrushEventListeners';

export type EntityBrushProps = {
    entity: EntityStore;
    attachedToRef: React.MutableRefObject<HTMLElement>;
};

const { styleSpan, styleComponent, styleDiv } = getStylistV2('EntityBrush');
const brushTheme = getScopedTheme('EntityBrush');

const Container = styleSpan('Container', theme => ({
    backgroundColor: theme.containerBackground,
    height: '28px',
    boxShadow: theme.containerShadow,
    borderRadius: '2px',
    transition: 'opacity 100ms linear',
    zIndex: theme.zIndex as Property.ZIndex,
    whiteSpace: 'nowrap',
    flexDirection: 'row',
    position: 'fixed',
    alignItems: 'center',
    display: 'flex'
}));

const BrushIcon = styleComponent<IIconProps>(Icon)('BrushIcon', theme => ({
    fontSize: '12px',
    margin: '8px 12px',
    color: theme.iconColor
}));

const Splitter = styleDiv('Splitter', theme => ({
    height: '100%',
    width: 1,
    backgroundColor: theme.splitterBackground
}));

const Circle = styleSpan(
    'Circle',
    (theme, cssVars) => ({
        height: '12px',
        width: '12px',
        border: theme.circleBorder,
        marginLeft: 12,
        borderRadius: '50%',
        backgroundColor: cssVars.backgroundColor
    }),
    {
        cssVars: { backgroundColor: '' },
        cssVarsSetter: ({ color }: { color: string }) => ({
            backgroundColor: color
        })
    }
);

const Text = styleComponent<LuisTextProps>(LuisText)('Text', {
    margin: '0px 12px 0 4px'
});

export const EntityBrush = observer((props: EntityBrushProps) => {
    const { entity, attachedToRef } = props;
    const ref = React.useRef<HTMLSpanElement>();

    useBrushEventListeners({
        attachedToRef,
        ref,
        getCoordinates: React.useCallback(e => ({ top: e.clientY - 38, left: e.clientX - 16 }), [])
    });

    return (
        <Container originalRef={ref} style={{ opacity: '0' }}>
            <BrushIcon iconName="Highlight" />
            <Splitter />
            <Circle customProps={{ color: entity.color }} />
            <Text size="12px" color={brushTheme.textColor}>
                {entity.name}
            </Text>
        </Container>
    );
});
